import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useClerk } from "@clerk/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

type Recovery = {
  ownerId: string | undefined;
  pending: boolean;
  failed: boolean;
  retry: () => Promise<void>;
};
const RecoveryContext = createContext<((value: Recovery | null) => void) | null>(null);

export function useSignOutRecovery() {
  return useContext(RecoveryContext);
}

/** Lives outside account-keyed providers and protected routes. Clerk can clear
 * its local user before provider revocation fails and unmount those routes. */
export function WebSignOutRecoveryProvider({ children }: { children: ReactNode }) {
  const clerk = useClerk();
  const { t } = useTranslation();
  const [recovery, setRecovery] = useState<Recovery | null>(null);
  const userId = clerk.user?.id;
  useEffect(() => {
    // Never let a retained callback sign out a newly signed-in member.
    if (userId && recovery?.ownerId && userId !== recovery.ownerId) setRecovery(null);
  }, [userId, recovery?.ownerId]);
  const showRecovery = Boolean(recovery && !userId && (recovery.failed || recovery.pending));
  return (
    <RecoveryContext.Provider value={setRecovery}>
      {children}
      <Dialog open={showRecovery}>
        <DialogContent
          data-testid="web-sign-out-recovery"
          className="[&>button.absolute]:hidden"
          onEscapeKeyDown={event => event.preventDefault()}
          onPointerDownOutside={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
        >
          <DialogTitle>{t("sidebar.signOut")}</DialogTitle>
          <DialogDescription role={recovery?.failed ? "alert" : undefined}>
            {recovery?.failed ? `${t("common.error")}. ${t("common.retry")}` : t("sidebar.signOut")}
          </DialogDescription>
          <Button disabled={recovery?.pending} onClick={() => void recovery?.retry()}>
            {t("common.retry")}
          </Button>
        </DialogContent>
      </Dialog>
    </RecoveryContext.Provider>
  );
}
